import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Wrench, SlidersHorizontal, ArrowRight, Clock, ShieldCheck, BadgeCheck } from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/groepenkast-keuze")({
  head: () => {
    const url = `${SITE_URL}/groepenkast-keuze`;
    const title = "Groepenkast samenstellen | Zelf of door Elvion Group";
    const description =
      "Kies zelf je AEG groepenkast samenstellen in onze configurator, of laat een erkende installateur van Elvion Group een passende offerte maken.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: GroepenkastKeuzePage,
});

function GroepenkastKeuzePage() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Hoe wil je je groepenkast samenstellen?
          </h1>
          <p className="mt-4 text-lg text-text-secondary">
            Kies de manier die het beste bij je past. Beide opties zijn vrijblijvend en worden uitgevoerd door erkende installateurs.
          </p>
        </div>

        <div className="mx-auto mt-10 grid w-full max-w-4xl gap-6 sm:grid-cols-2">
          {/* Zelf samenstellen */}
          <div className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:border-orange/40 hover:shadow-md sm:p-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange/10 text-orange">
              <SlidersHorizontal className="size-6" aria-hidden="true" />
            </div>
            <h2 className="mt-5 text-xl font-bold text-foreground">Zelf samenstellen</h2>
            <p className="mt-2 flex-1 text-text-secondary">
              Gebruik onze online AEG-groepenkastconfigurator. Kies je kast, groepen en aardlekschakelaars en vraag direct een offerte aan.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-text-secondary">
              <li className="flex items-center gap-2">
                <BadgeCheck className="size-4 shrink-0 text-grass" aria-hidden="true" />
                Direct zichtbaar resultaat
              </li>
              <li className="flex items-center gap-2">
                <BadgeCheck className="size-4 shrink-0 text-grass" aria-hidden="true" />
                Conform NEN 1010
              </li>
              <li className="flex items-center gap-2">
                <BadgeCheck className="size-4 shrink-0 text-grass" aria-hidden="true" />
                Binnen 1 werkdag offerte
              </li>
            </ul>
            <Button variant="orange" size="lg" asChild className="mt-6 w-full">
              <Link to="/groepenkast-configurator" className="inline-flex items-center justify-center gap-2">
                Zelf samenstellen
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>

          {/* Laten samenstellen */}
          <div className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:border-primary/40 hover:shadow-md sm:p-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Wrench className="size-6" aria-hidden="true" />
            </div>
            <h2 className="mt-5 text-xl font-bold text-foreground">Laten samenstellen</h2>
            <p className="mt-2 flex-1 text-text-secondary">
              Laat onze erkende installateurs een groepenkast op maat voor je ontwerpen. Je ontvangt een uitgewerkt voorstel inclusief materiaal en montage.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-text-secondary">
              <li className="flex items-center gap-2">
                <ShieldCheck className="size-4 shrink-0 text-grass" aria-hidden="true" />
                Advies op afstand of ter plaatse
              </li>
              <li className="flex items-center gap-2">
                <Clock className="size-4 shrink-0 text-grass" aria-hidden="true" />
                Binnen 1 werkdag offerte
              </li>
              <li className="flex items-center gap-2">
                <BadgeCheck className="size-4 shrink-0 text-grass" aria-hidden="true" />
                Inclusief montage en garantie
              </li>
            </ul>
            <Button variant="default" size="lg" asChild className="mt-6 w-full">
              <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }} className="inline-flex items-center justify-center gap-2">
                Laten samenstellen
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-text-secondary">
          Twijfel je? Kies "Laten samenstellen" en een van onze adviseurs neemt contact met je op om de mogelijkheden door te nemen.
        </p>
      </main>
      <Footer />
    </div>
  );
}
